/*
  House of Raaga — search overlay behaviour.
  Markup: sections/raaga-search.liquid. Results: sections/raaga-predictive-search.liquid.

  One request per settled keystroke. /search/suggest caps resources[limit] at 10 and applies
  it to every requested type alike, so the fetch asks for the largest of the three limits and
  the groups are trimmed to their own limit here — one round trip instead of three.
*/
(function () {
  var RECENT_KEY = 'rg:recent';
  var DEBOUNCE = 220;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var delegated = false;

  /* localStorage throws in Safari private mode; recents are a nicety, never a blocker */
  function readRecent() {
    try {
      var raw = window.localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (t) { return typeof t === 'string' && t; }) : [];
    } catch (e) { return []; }
  }

  function writeRecent(list) {
    try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch (e) {}
  }

  function init(root) {
    var el = (root || document).querySelector('[data-rg-search]');
    if (!el || el.dataset.rgBound) return;
    el.dataset.rgBound = 'true';

    var panel = el.querySelector('.rg-sr__panel');
    var form = el.querySelector('.rg-sr__bar');
    var input = el.querySelector('.rg-sr__in');
    var idle = el.querySelector('[data-rg-sr-idle]');
    var out = el.querySelector('[data-rg-sr-out]');
    var spin = el.querySelector('[data-rg-sr-spin]');
    var status = el.querySelector('[data-rg-sr-status]');
    var recentBox = el.querySelector('[data-rg-sr-recent]');
    var recentList = el.querySelector('[data-rg-sr-recent-list]');
    var searchUrl = form.getAttribute('action');
    var searchForText = el.dataset.searchFor || 'Search for [terms]';

    var endpoint = el.dataset.endpoint || '/search/suggest';
    var minChars = parseInt(el.dataset.minChars, 10) || 2;
    var recentMax = parseInt(el.dataset.recent, 10) || 0;

    var limits = {
      product: parseInt(el.dataset.limitProduct, 10) || 0,
      collection: parseInt(el.dataset.limitCollection, 10) || 0,
      page: parseInt(el.dataset.limitPage, 10) || 0
    };

    /* pages and articles share one group and one limit */
    var types = [];
    if (limits.product > 0) types.push('product');
    if (limits.collection > 0) types.push('collection');
    if (limits.page > 0) types.push('page', 'article');

    var fetchLimit = Math.min(10, Math.max(limits.product, limits.collection, limits.page, 1));

    var opener = null;
    var suppressFocus = false;
    var timer = null;
    var ctrl = null;
    var lastTerm = null;
    var active = -1;

    /* -------------------------------------------------------------- open */
    function open(trigger) {
      if (el.classList.contains('on')) return;
      opener = trigger || null;

      /* a trigger that carries a term hands it over: the results page field opens the overlay
         mid-search, and retyping what you already typed would be a poor trade for a modal. */
      var seed = '';
      if (trigger) seed = trigger.value || trigger.getAttribute('data-rg-search-term') || '';
      seed = seed.trim();
      if (seed) input.value = seed;

      el.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey);
      paintRecent();
      requestAnimationFrame(function () {
        el.classList.add('on');
        input.focus();
        input.select();
        if (seed.length >= minChars) { lastTerm = seed; run(seed); }
      });
    }

    function close() {
      if (!el.classList.contains('on')) return;
      el.classList.remove('on');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      if (ctrl) { ctrl.abort(); ctrl = null; }
      clearTimeout(timer);

      var done = false;
      function finish() {
        if (done) return;
        done = true;
        el.hidden = true;
        if (opener && document.contains(opener)) {
          /* Restoring focus to a field that opens on focus would reopen the overlay. The guard
             covers exactly that call and nothing more: focusin fires synchronously inside
             focus(), so clearing on the next tick is enough. An arbitrary timeout here is worse
             than useless — finish() itself is deferred, so the window drifts with it and eats
             focus the shopper meant. */
          suppressFocus = true;
          opener.focus();
          setTimeout(function () { suppressFocus = false; }, 0);
        }
        opener = null;
      }
      /* transitionend can be skipped outright under reduced motion — always keep a guard */
      panel.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, reduce ? 0 : 420);
    }

    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); move(1); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); return; }
      if (e.key === 'Enter') {
        var opts = options();
        if (active > -1 && opts[active]) { e.preventDefault(); opts[active].click(); }
      }
    }

    /* --------------------------------------------------------- keyboard */
    function options() {
      var scope = out.children.length ? out : idle;
      return scope ? Array.prototype.slice.call(scope.querySelectorAll('[data-rg-sr-opt], .rg-sr__pill')) : [];
    }

    function move(step) {
      var opts = options();
      if (!opts.length) return;
      if (active > -1 && opts[active]) {
        opts[active].classList.remove('is-on');
        if (opts[active].hasAttribute('aria-selected')) opts[active].setAttribute('aria-selected', 'false');
      }
      active += step;
      if (active >= opts.length) active = 0;
      if (active < 0) active = opts.length - 1;
      var next = opts[active];
      if (!next.id) next.id = 'RgSrOpt-' + active;
      next.classList.add('is-on');
      if (next.hasAttribute('aria-selected')) next.setAttribute('aria-selected', 'true');
      next.scrollIntoView({ block: 'nearest' });
      input.setAttribute('aria-activedescendant', next.id);
    }

    function resetActive() {
      active = -1;
      input.removeAttribute('aria-activedescendant');
    }

    /* ----------------------------------------------------------- recents */
    function remember(term) {
      if (!recentMax || !term) return;
      var list = readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      list.unshift(term);
      writeRecent(list.slice(0, recentMax));
    }

    function paintRecent() {
      if (!recentBox || !recentList) return;
      var list = readRecent().slice(0, recentMax);
      recentList.textContent = '';
      list.forEach(function (term) {
        var a = document.createElement('a');
        a.className = 'rg-sr__pill';
        a.href = searchUrl + '?q=' + encodeURIComponent(term) + '&options[prefix]=last';
        a.textContent = term;
        a.setAttribute('data-rg-sr-term', term);
        recentList.appendChild(a);
      });
      recentBox.hidden = list.length === 0;
    }

    /* ------------------------------------------------------------- fetch */
    function trim(group, limit) {
      var box = out.querySelector('[data-rg-grp="' + group + '"]');
      if (!box) return;
      if (limit <= 0) { box.remove(); return; }
      var rows = box.querySelectorAll('.rg-sr__list > li');
      for (var i = limit; i < rows.length; i++) rows[i].remove();
    }

    function showIdle() {
      out.textContent = '';
      if (idle) idle.hidden = false;
      spin.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      resetActive();
      lastTerm = null;
    }

    function run(term) {
      if (ctrl) ctrl.abort();
      ctrl = typeof AbortController === 'function' ? new AbortController() : null;

      var url = endpoint +
        '?q=' + encodeURIComponent(term) +
        '&section_id=raaga-predictive-search' +
        '&resources[type]=' + types.join(',') +
        '&resources[limit]=' + fetchLimit +
        '&resources[limit_scope]=each';

      spin.hidden = false;

      fetch(url, ctrl ? { signal: ctrl.signal } : undefined)
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
        .then(function (html) {
          if (term !== input.value.trim()) return;
          /* the section renders nothing when predictive_search.performed is false */
          if (!html || html.indexOf('RgSearchResults') === -1) { showIdle(); return; }
          out.innerHTML = html;
          trim('collection', limits.collection);
          trim('product', limits.product);
          trim('content', limits.page);
          if (idle) idle.hidden = true;
          spin.hidden = true;
          input.setAttribute('aria-expanded', 'true');
          resetActive();
          var count = out.querySelector('[data-rg-sr-count]');
          if (status && count) status.textContent = count.textContent.trim();
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          /* Never fail silently back to the idle state: a 404 on the section, a dropped
             connection and "nothing matched" would all look identical, to the shopper and to
             anyone debugging this. Show the escape hatch instead — it always goes somewhere. */
          spin.hidden = true;
          if (idle) idle.hidden = true;
          out.textContent = '';
          var a = document.createElement('a');
          a.className = 'rg-sr__all';
          a.href = searchUrl + '?q=' + encodeURIComponent(term) + '&options[prefix]=last';
          a.setAttribute('data-rg-sr-opt', '');
          a.textContent = searchForText.replace('[terms]', term);
          out.appendChild(a);
          input.setAttribute('aria-expanded', 'true');
          resetActive();
          if (status) status.textContent = a.textContent;
        });
    }

    /* ------------------------------------------------------------- wiring */
    input.addEventListener('input', function () {
      var term = input.value.trim();
      clearTimeout(timer);
      if (term.length < minChars) {
        if (ctrl) { ctrl.abort(); ctrl = null; }
        showIdle();
        return;
      }
      if (term === lastTerm) return;
      lastTerm = term;
      timer = setTimeout(function () { run(term); }, DEBOUNCE);
    });

    form.addEventListener('submit', function () { remember(input.value.trim()); });

    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-rg-sr-clear]')) {
        e.preventDefault();
        writeRecent([]);
        paintRecent();
        return;
      }
      var pill = e.target.closest('.rg-sr__pill');
      if (pill) { remember(pill.getAttribute('data-rg-sr-term')); return; }
      /* a row records what was typed, not its own title: a browse row was never searched for,
         so input.value is empty there and nothing is recorded. */
      var row = e.target.closest('[data-rg-sr-opt]');
      if (row) { remember(input.value.trim()); return; }
      /* anything outside the panel closes, rather than the scrim element specifically: the
         scrim is one stylesheet rule away from not being the hit target at all. */
      if (e.target.closest('[data-rg-sr-close]') || !e.target.closest('.rg-sr__panel')) {
        e.preventDefault();
        close();
      }
    });

    /* delegated once for the whole document: a section reload in the theme editor rebuilds
       the overlay, and a per-init listener would stack up another copy each time */
    if (!delegated) {
      delegated = true;
      document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-rg-search-open]');
        if (!trigger) return;
        var live = document.querySelector('[data-rg-search]');
        if (!live) return;
        e.preventDefault();
        (live.rgOpen || open)(trigger);
      });

      document.addEventListener('focusin', function (e) {
        var field = e.target.closest('input[data-rg-search-open]');
        if (!field) return;
        var live = document.querySelector('[data-rg-search]');
        /* no overlay on the page (raaga_chrome off) means the field stays an ordinary input */
        if (!live || live.rgSuppressed && live.rgSuppressed()) return;
        field.blur();
        (live.rgOpen || open)(field);
      });
    }
    el.rgOpen = open;
    el.rgSuppressed = function () { return suppressFocus; };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
